import * as z from 'zod';
import { t } from 'i18next';
import { components } from 'src/generated/v1';

export type Term = components['schemas']['Term'];
export type RegistrationDocument = components['schemas']['CompanyRegistrationDocumentMetadata'];
export type CompanyRegistrationUpdate = components['schemas']['CompanyRegistrationUpdate'];
export type RegistrationStatus = NonNullable<CompanyRegistrationUpdate['status']>;

export const REGISTRATION_DOCUMENT_TYPES = {
  LOGO: 'logo',
  ID_DOCUMENT: 'id_document',
  OTHER: 'other',
} as const;

export type RegistrationDocumentType = (typeof REGISTRATION_DOCUMENT_TYPES)[keyof typeof REGISTRATION_DOCUMENT_TYPES];

export const MAX_DOCUMENT_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_DOCUMENT_COUNT = 5;

const requiredString = (msg: string) =>
  z
    .string({ required_error: msg })
    .nullable()
    .refine(v => !!v && v.trim().length > 0, { message: msg });

const optionalString = z.string().nullable().optional();

export const addressSchema = z.object({
  street: requiredString(t('registration.errors.street_required')),
  zip_code: requiredString(t('registration.errors.zip_required')),
  city: requiredString(t('registration.errors.city_required')),
  country_code: requiredString(t('registration.errors.country_required')),
  country_name: optionalString,
  street2: optionalString,
});

export type AddressFormValues = z.infer<typeof addressSchema>;

export const remissionAddressSchema = z.object({
  company: requiredString(t('registration.errors.remission_company_required')),
  address: addressSchema,
});

export type RemissionAddressFormValues = z.infer<typeof remissionAddressSchema>;

export const emailSchema = z.object({
  email: z.string({ required_error: t('errors.invalid_email') }).email({ message: t('errors.invalid_email') }),
});

export type EmailFormValues = z.infer<typeof emailSchema>;

export const WAREHOUSE_OPTIONS = ['WSAT', 'WSDE'] as const;
export type WarehouseCode = (typeof WAREHOUSE_OPTIONS)[number];

const warehouseSchema = z
  .string()
  .nullable()
  .refine(v => !!v && (WAREHOUSE_OPTIONS as readonly string[]).includes(v), {
    message: t('registration.errors.warehouse_required'),
  });

export const companyDataSchema = z.object({
  company_name: requiredString(t('registration.errors.company_name_required')),
  company_tax_id: requiredString(t('registration.errors.tax_id_required')),
  company_bin: requiredString(t('registration.errors.bin_required')),
  warehouse: warehouseSchema,
  address: addressSchema,
  remission_address: remissionAddressSchema,
});

export type CompanyDataFormValues = z.infer<typeof companyDataSchema>;

export const verificationSchema = z.object({
  partner_name: requiredString(t('registration.errors.partner_name_required')),
  partner_position: requiredString(t('registration.errors.partner_position_required')),
  partner_email: z.string({ required_error: t('errors.invalid_email') }).email({ message: t('errors.invalid_email') }),
  partner_phone: requiredString(t('registration.errors.partner_phone_required')),
});

export type VerificationFormValues = z.infer<typeof verificationSchema>;

export const bankAndTermsSchema = z
  .object({
    payment_terms: z.enum(['debit', 'invoice'] as const, {
      required_error: t('registration.errors.payment_terms_required'),
    }),
    bank_account: z.object({
      bank_name: optionalString,
      iban: optionalString,
      bic: optionalString,
    }),
    accepted_term_ids: z.array(z.number()),
  })
  .superRefine((values, ctx) => {
    if (values.payment_terms === 'debit') {
      const bank = values.bank_account ?? {};
      if (!bank.bank_name || bank.bank_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bank_account', 'bank_name'],
          message: t('registration.errors.bank_name_required'),
        });
      }
      if (!bank.iban || bank.iban.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bank_account', 'iban'],
          message: t('registration.errors.iban_required'),
        });
      }
      if (!bank.bic || bank.bic.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bank_account', 'bic'],
          message: t('registration.errors.bic_required'),
        });
      }
    }
  });

export type BankAndTermsFormValues = z.infer<typeof bankAndTermsSchema>;
